import { useState, type FormEvent } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';

interface Props {
    onClose: () => void;
    onInvited: () => void;
}

type ScopeType = 'all_content' | 'md_only' | 'folders';

interface GrantInput {
    repoId: string;
    scopeType: ScopeType;
    folders: string;
}

export function InviteUserModal({ onClose, onInvited }: Props) {
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [globalRole, setGlobalRole] = useState<'reviewer' | 'admin'>('reviewer');
    const [grants, setGrants] = useState<GrantInput[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    function addGrant() {
        setGrants((g) => [...g, { repoId: '', scopeType: 'all_content', folders: '' }]);
    }
    function updateGrant(i: number, patch: Partial<GrantInput>) {
        setGrants((g) => g.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
    }
    function removeGrant(i: number) {
        setGrants((g) => g.filter((_, idx) => idx !== i));
    }

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setError('Valid email is required');
            return;
        }
        for (const g of grants) {
            if (!g.repoId.match(/^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/)) {
                setError(`Each grant repoId must be in form owner/repo (got "${g.repoId}")`);
                return;
            }
            if (g.scopeType === 'folders' && g.folders.trim() === '') {
                setError(`Folders scope on ${g.repoId} needs at least one folder`);
                return;
            }
        }
        setSubmitting(true);
        setError(null);
        setSuccess(null);
        const body = {
            email,
            displayName: displayName || undefined,
            globalRole,
            grants: grants.map((g) => ({
                repoId: g.repoId,
                scope:
                    g.scopeType === 'folders'
                        ? { type: 'folders' as const, folders: g.folders.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) }
                        : g.scopeType === 'md_only'
                          ? { type: 'md_only' as const }
                          : { type: 'all_content' as const }
            }))
        };
        try {
            await apiCall('/manage/users', { method: 'POST', body });
            setSuccess(`Invited ${email}. They'll be activated on first sign-in.`);
            onInvited();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose} role="presentation">
            <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <header className="modal-header">
                    <h2>Invite user</h2>
                    <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
                </header>
                <form onSubmit={handleSubmit} className="modal-body">
                    <label className="form-group">
                        <span className="form-label">Email</span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="mary@bacr.org"
                            autoFocus
                            required
                        />
                    </label>
                    <label className="form-group">
                        <span className="form-label">Display name (optional)</span>
                        <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Mary Moeller"
                        />
                    </label>
                    <label className="form-group">
                        <span className="form-label">Role</span>
                        <select value={globalRole} onChange={(e) => setGlobalRole(e.target.value as 'reviewer' | 'admin')}>
                            <option value="reviewer">Reviewer</option>
                            <option value="admin">Admin</option>
                        </select>
                    </label>
                    <div className="form-group">
                        <div className="form-group__header">
                            <span className="form-label">Repo grants</span>
                            <button type="button" className="btn btn--ghost btn--small" onClick={addGrant}>+ Add grant</button>
                        </div>
                        {grants.length === 0 && <p className="form-hint">No grants. Add at least one for reviewers (admins see all repos).</p>}
                        {grants.map((g, i) => (
                            <div key={i} className="grant-row">
                                <input
                                    type="text"
                                    value={g.repoId}
                                    onChange={(e) => updateGrant(i, { repoId: e.target.value })}
                                    placeholder="owner/repo"
                                />
                                <select value={g.scopeType} onChange={(e) => updateGrant(i, { scopeType: e.target.value as ScopeType })}>
                                    <option value="all_content">All content</option>
                                    <option value="md_only">Markdown only</option>
                                    <option value="folders">Specific folders</option>
                                </select>
                                {g.scopeType === 'folders' && (
                                    <textarea
                                        value={g.folders}
                                        onChange={(e) => updateGrant(i, { folders: e.target.value })}
                                        placeholder="docs/&#10;runbooks/auth/"
                                        rows={2}
                                    />
                                )}
                                <button type="button" className="btn btn--ghost btn--small" onClick={() => removeGrant(i)} aria-label="Remove grant">×</button>
                            </div>
                        ))}
                    </div>
                    {error && <div className="form-error">{error}</div>}
                    {success && <div className="form-success">{success}</div>}
                    <footer className="modal-footer">
                        <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
                            {success ? 'Close' : 'Cancel'}
                        </button>
                        {!success && (
                            <button type="submit" className="btn btn--primary" disabled={submitting}>
                                {submitting ? 'Inviting…' : 'Invite'}
                            </button>
                        )}
                    </footer>
                </form>
            </div>
        </div>
    );
}
